// packages/web/src/context/CartContext.tsx
import React, { createContext, useContext, useState, useEffect } from 'react'
import { useSiteConfig } from './SiteConfigContext'
import { isGstEnabled } from '../lib/companyGst'
import {
  DEFAULT_FREE_DELIVERY_MIN,
  DEFAULT_STANDARD_SHIPPING,
  parseMoneySetting,
  shippingAmountForSubtotal,
} from '@buddys-choice/shared'

export interface CartItem {
  productId: string
  variantId?: string | undefined
  comboOfferId?: string | undefined
  productName: string
  productImage?: string | undefined
  variantDescription?: string | undefined
  unitPrice: number
  quantity: number
  stockQty?: number
  isPreOrder?: boolean
}

interface CartContextType {
  items: CartItem[]
  addItem: (item: Omit<CartItem, 'quantity'> & { quantity?: number }) => void
  removeItem: (productId: string, variantId?: string, comboOfferId?: string) => void
  updateQuantity: (productId: string, variantId: string | undefined, quantity: number, comboOfferId?: string) => void
  clearCart: () => void
  isCartOpen: boolean
  setIsCartOpen: (open: boolean) => void
  subtotal: number
  taxCgst: number
  taxSgst: number
  shippingAmount: number
  freeDeliveryMin: number
  total: number
  totalItemsCount: number
}

const CartContext = createContext<CartContextType | undefined>(undefined)

const STORAGE_KEY = 'buddys_cart'

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { config } = useSiteConfig()
  const gstOn = isGstEnabled(config)

  const [items, setItems] = useState<CartItem[]>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY)
      return saved ? JSON.parse(saved) : []
    } catch {
      return []
    }
  })
  const [isCartOpen, setIsCartOpen] = useState(false)

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items))
    } catch (e) {
      console.error('Failed to save cart to localStorage:', e)
    }
  }, [items])

  const addItem = (newItem: Omit<CartItem, 'quantity'> & { quantity?: number }) => {
    const maxQty =
      newItem.isPreOrder || newItem.stockQty == null || newItem.stockQty <= 0
        ? 99999
        : newItem.stockQty
    const qty = Math.min(newItem.quantity ?? 1, maxQty)
    setItems((prev) => {
      const existingIndex = prev.findIndex((i) =>
        newItem.comboOfferId
          ? i.comboOfferId === newItem.comboOfferId
          : i.productId === newItem.productId && i.variantId === newItem.variantId && !i.comboOfferId,
      )

      if (existingIndex > -1) {
        const updated = [...prev]
        const current = updated[existingIndex]!
        const cap =
          current.isPreOrder || current.stockQty == null || current.stockQty <= 0
            ? 99999
            : current.stockQty
        updated[existingIndex] = {
          ...current,
          quantity: Math.min(current.quantity + qty, cap),
        }
        return updated
      }

      return [...prev, { ...newItem, quantity: qty }]
    })
    setIsCartOpen(true)
  }

  const removeItem = (productId: string, variantId?: string, comboOfferId?: string) => {
    setItems((prev) =>
      prev.filter((i) => {
        if (comboOfferId) return i.comboOfferId !== comboOfferId
        return !(i.productId === productId && i.variantId === variantId && !i.comboOfferId)
      }),
    )
  }

  const updateQuantity = (productId: string, variantId: string | undefined, quantity: number, comboOfferId?: string) => {
    if (quantity <= 0) {
      removeItem(productId, variantId, comboOfferId)
      return
    }

    setItems((prev) =>
      prev.map((item) => {
        const match = comboOfferId
          ? item.comboOfferId === comboOfferId
          : item.productId === productId && item.variantId === variantId && !item.comboOfferId
        if (match) {
          const cap =
            item.isPreOrder || item.stockQty == null || item.stockQty <= 0 ? 99999 : item.stockQty
          return { ...item, quantity: Math.min(quantity, cap) }
        }
        return item
      }),
    )
  }

  const clearCart = () => {
    setItems([])
  }

  const subtotal = items.reduce((sum, item) => sum + item.unitPrice * item.quantity, 0)
  const taxCgst = gstOn ? Math.round(subtotal * 0.03 * 100) / 100 : 0
  const taxSgst = gstOn ? Math.round(subtotal * 0.03 * 100) / 100 : 0
  const freeDeliveryMin = parseMoneySetting(config.free_delivery_min_amount, DEFAULT_FREE_DELIVERY_MIN)
  const standardShipping = parseMoneySetting(config.standard_shipping_amount, DEFAULT_STANDARD_SHIPPING)
  const shippingAmount = shippingAmountForSubtotal(subtotal, freeDeliveryMin, standardShipping)
  const total = subtotal + taxCgst + taxSgst + shippingAmount
  const totalItemsCount = items.reduce((sum, item) => sum + item.quantity, 0)

  return (
    <CartContext.Provider
      value={{
        items,
        addItem,
        removeItem,
        updateQuantity,
        clearCart,
        isCartOpen,
        setIsCartOpen,
        subtotal,
        taxCgst,
        taxSgst,
        shippingAmount,
        freeDeliveryMin,
        total,
        totalItemsCount,
      }}
    >
      {children}
    </CartContext.Provider>
  )
}

export const useCart = () => {
  const context = useContext(CartContext)
  if (!context) {
    throw new Error('useCart must be used within a CartProvider')
  }
  return context
}
